import React, { useEffect, useState, useRef } from 'react';
import { PixelGlyph } from './PixelGlyph';
import { Activity, Cpu, Gauge, Layers, Terminal, X, ChevronUp } from 'lucide-react';

interface CraftInspectorProps {
  // Optional override props for backward compatibility
  scrollVelocity?: number;
  activeSection?: string;
}

export const CraftInspector: React.FC<CraftInspectorProps> = ({
  scrollVelocity: propScrollVelocity,
  activeSection: propActiveSection,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [fps, setFps] = useState<number>(120);
  const [frameTime, setFrameTime] = useState<number>(8.3);
  const [internalVelocity, setInternalVelocity] = useState<number>(0);
  const [internalSection, setInternalSection] = useState<string>('HERO_CORE');

  const frameCountRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(performance.now());
  const lastScrollYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

  const scrollVelocity = propScrollVelocity !== undefined ? propScrollVelocity : internalVelocity;
  const activeSection = propActiveSection !== undefined ? propActiveSection : internalSection;

  const isWebGL = typeof window !== 'undefined' && (() => {
    if (window.location.search.includes('nowebgl') || window.location.hash.includes('nowebgl')) {
      return false;
    }
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl') || (c.getContext('experimental-webgl') as any)));
    } catch {
      return false;
    }
  })();

  // 1. Observe active section locally without re-rendering the parent App
  useEffect(() => {
    const sectionIds = [
      'hero',
      'manifesto',
      'specimens',
      'architecture',
      'playground',
      'benchmarks',
      'voices',
      'contact',
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInternalSection(entry.target.id.toUpperCase());
          }
        });
      },
      { threshold: 0.25 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // 2. Real-time FPS & Scroll Velocity calculation loop (Isolated in HUD only)
  useEffect(() => {
    let animId: number;

    const measureLoop = (now: number) => {
      frameCountRef.current++;
      const elapsed = now - lastTimeRef.current;

      // Measure scroll velocity directly
      const currentScrollY = window.scrollY;
      const scrollDiff = Math.abs(currentScrollY - lastScrollYRef.current);
      lastScrollYRef.current = currentScrollY;

      if (elapsed >= 500) {
        const currentFps = Math.round((frameCountRef.current * 1000) / elapsed);
        setFps(Math.min(120, Math.max(30, currentFps)));
        setFrameTime(Math.round((1000 / currentFps) * 10) / 10);
        frameCountRef.current = 0;
        lastTimeRef.current = now;

        // Smooth velocity sample
        const sampledVelocity = Math.round(((scrollDiff * 1000) / elapsed) * 10) / 10;
        setInternalVelocity(sampledVelocity);
      }

      animId = requestAnimationFrame(measureLoop);
    };

    animId = requestAnimationFrame(measureLoop);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <aside
      aria-label="Engine Telemetry HUD"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 font-mono select-none"
    >
      {/* Minimized Pill Toggle */}
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full bg-espresso-900/90 border border-white/10 hover:border-sunset-gold/50 shadow-bezel-outer text-xs transition-all duration-300 group cursor-pointer"
          data-cursor="inspect"
        >
          <PixelGlyph name="pulse-dot" size={10} />
          <span className="text-cream-300 text-[10px] sm:text-[11px] tracking-wider font-semibold">CRAFT HUD</span>
          <div className="h-3 w-px bg-white/15" />
          <span className="text-sunset-gold font-bold text-[10px] sm:text-[11px]">{fps} FPS</span>
          <ChevronUp className="w-3.5 h-3.5 text-cream-muted transition-transform group-hover:-translate-y-0.5" />
        </button>
      ) : (
        /* Expanded High-Tech Diagnostic Panel */
        <div className="w-[calc(100vw-2rem)] max-w-xs rounded-2xl bg-[#0e0e13]/95 border border-white/10 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Panel Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-espresso-900/60">
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4 text-sunset-gold" />
              <span className="text-xs font-semibold tracking-wider text-cream-100">
                TELEMETRY // SPECIMEN 01
              </span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1 rounded-md hover:bg-white/10 text-cream-muted hover:text-cream-100 transition-colors"
              aria-label="Close Inspector"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Metrics Matrix */}
          <div className="p-4 space-y-3.5 text-xs">
            {/* FPS & Frametime */}
            <div className="flex items-center justify-between">
              <span className="text-cream-muted flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-sunset-gold" />
                FRAME RATE
              </span>
              <div className="flex items-center gap-2 font-bold">
                <span className="text-sunset-gold">{fps} FPS</span>
                <span className="text-cream-dim text-[10px]">({frameTime}ms)</span>
              </div>
            </div>

            {/* Lenis Velocity */}
            <div className="flex items-center justify-between">
              <span className="text-cream-muted flex items-center gap-1.5">
                <Gauge className="w-3.5 h-3.5 text-sunset-orange" />
                SCROLL VELOCITY
              </span>
              <span className="text-cream-100 font-semibold">
                {Math.abs(scrollVelocity).toFixed(1)} px/s
              </span>
            </div>

            {/* Three.js Particle Manifold / CSS Fallback */}
            <div className="flex items-center justify-between">
              <span className="text-cream-muted flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5 text-sunset-flame" />
                {isWebGL ? 'WEBGL MESH' : 'CSS MESH'}
              </span>
              <span className="text-sunset-flame font-semibold">
                {isWebGL ? '6.5K 120FPS GPU' : 'CSS AMBIENT MESH'}
              </span>
            </div>

            {/* Active GSAP Section */}
            <div className="flex items-center justify-between">
              <span className="text-cream-muted flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-gold-champagne" />
                VIEWPORT TRIGGER
              </span>
              <span className="text-gold-radiance text-[11px] font-semibold uppercase truncate max-w-[130px]">
                {activeSection}
              </span>
            </div>

            {/* Domain Verification Footnote */}
            <div className="pt-2.5 border-t border-white/10 flex items-center justify-between text-[10px] text-cream-dim">
              <span>TARGET DOMAIN</span>
              <span className="text-cream-200 font-semibold">example.th157le.ru</span>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
